import { History } from "history";
import { IBlackUser, ISearchUser } from "../../../../store/types/userTypes";
import { RoutePaths } from "../../../../utils/Items";
import { useRegistroIngresoSession } from "../session/RegistroIngresoSessionContext";
import {
  RegistrationMode,
  RegistroIngresoNavigationIntent,
  RegistroIngresoNavigationState,
} from "../registroIngresoNavigation";
import { isFullAccessRestriction } from "./blacklistRestriction";

export type PendingPassScanData = {
  pass: string;
  passData: any;
  guest: any;
};

type SessionApi = ReturnType<typeof useRegistroIngresoSession>;

type ProcessScanArgs = {
  results: ISearchUser;
  mode: RegistrationMode;
  intent: RegistroIngresoNavigationIntent;
  session: SessionApi;
  authStore: any;
  history: History;
  onBuildingMismatch: (pending: PendingPassScanData) => void;
  onError: (message: string) => void;
  onFullyBlocked?: (blacklistedData: IBlackUser) => void;
};

export const processRegistroIngresoScan = async ({
  results,
  mode,
  intent,
  session,
  authStore,
  history,
  onBuildingMismatch,
  onError,
  onFullyBlocked,
}: ProcessScanArgs): Promise<void> => {
  const documentNumber = results.dni?.trim();
  const documentCountry = results.countryCode;
  const isMrzResult = Boolean(results.firstname && results.lastname);
  const isQrResult = Boolean(results.rawQr);
  const isPassResult = Boolean(results.rawPass);

  if (!isQrResult && !isPassResult && (!documentNumber || !documentCountry)) return;

  const {
    appendTab,
    searchUser,
    searchUserByMrz,
    searchUserByQr,
    searchUserByPass,
    clearSession,
    setActiveTab,
    findExistingTab,
    closeTab,
  } = session;

  try {
    if (isQrResult) {
      const userData = results.userData;
      if (!userData) throw new Error("Usuario no encontrado");

      const userDni = userData.dni?.trim();
      const userCountry = userData.countryCode;

      let existingTab = null;
      if (userDni && userCountry) {
        existingTab = findExistingTab(userDni, userCountry, mode);
      }

      if (existingTab) {
        if (isFullAccessRestriction(existingTab.values?.blacklistedData)) {
          onFullyBlocked?.(existingTab.values.blacklistedData!);
          return;
        }
        setActiveTab(existingTab.id);
      } else {
        if (intent === "replace-session") {
          clearSession();
        }

        const userPrefill = {
          id: userData.id,
          firstName: userData.firstname || "",
          lastName: userData.lastname || "",
          documentNumber: userDni || "",
          documentCountry: userCountry || "",
          email: userData.email || "",
          phone: userData.phone || "",
          visitorPhoto: userData.facePhoto || null,
          frontDNI: userData.frontDNI || null,
          backDNI: userData.backDNI || null,
        };

        const tabId = appendTab(userPrefill, mode);
        const enrichedUser = await searchUserByQr(
          tabId,
          results.rawQr!,
          undefined,
          undefined,
          results.userData
        );
        if (isFullAccessRestriction(enrichedUser?.blacklistedData)) {
          closeTab(tabId);
          onFullyBlocked?.(enrichedUser.blacklistedData);
          return;
        }
      }
    } else if (isPassResult) {
      const passData = results.userData;
      const guest = passData?.guest || passData?.guests?.[0] || passData;
      if (!guest) throw new Error("Pase no encontrado");

      const guestDni = guest.dni?.trim();
      const guestCountry = guest.countryCode;

      let existingTab = null;
      if (guestDni && guestCountry) {
        existingTab = findExistingTab(guestDni, guestCountry, mode);
      }

      if (existingTab) {
        if (isFullAccessRestriction(existingTab.values?.blacklistedData)) {
          onFullyBlocked?.(existingTab.values.blacklistedData!);
          return;
        }
        setActiveTab(existingTab.id);
      } else {
        const officeBuildings = authStore?.buildings || [];
        const passBuildingId = passData?.building;
        const matchesBuilding = officeBuildings.some(
          (b: any) => String(b) === String(passBuildingId)
        );

        if (!matchesBuilding && passBuildingId) {
          onBuildingMismatch({
            pass: results.rawPass!,
            passData,
            guest,
          });
          return;
        }

        if (intent === "replace-session") {
          clearSession();
        }

        const guestPrefill = {
          id: guest.id,
          firstName: guest.firstname || "",
          lastName: guest.lastname || "",
          documentNumber: guest.dni || "",
          documentCountry: guest.countryCode || "",
          email: guest.email || "",
          phone: guest.phone || "",
          visitorPhoto: guest.facePhoto || null,
          frontDNI: guest.frontDNI || null,
          backDNI: guest.backDNI || null,
        };

        const tabId = appendTab(guestPrefill, mode);
        const passDataWithFlag = {
          ...passData,
          isBuildingMismatch: !matchesBuilding,
        };
        const enrichedUser = await searchUserByPass(
          tabId,
          results.rawPass!,
          undefined,
          undefined,
          passDataWithFlag
        );
        if (isFullAccessRestriction(enrichedUser?.blacklistedData)) {
          closeTab(tabId);
          onFullyBlocked?.(enrichedUser.blacklistedData);
          return;
        }
      }
    } else {
      let existingTab = null;
      if (documentNumber && documentCountry) {
        existingTab = findExistingTab(documentNumber, documentCountry, mode);
      }

      if (existingTab) {
        if (isFullAccessRestriction(existingTab.values?.blacklistedData)) {
          onFullyBlocked?.(existingTab.values.blacklistedData!);
          return;
        }
        setActiveTab(existingTab.id);
      } else {
        if (intent === "replace-session") {
          clearSession();
        }

        const tabId = appendTab(
          {
            documentCountry,
            documentNumber,
            firstName: results.firstname || "",
            lastName: results.lastname || "",
          },
          mode
        );

        const enrichedUser = isMrzResult
          ? await searchUserByMrz(tabId, {
              dni: documentNumber!,
              country: documentCountry!,
              firstname: results.firstname!,
              lastname: results.lastname!,
            })
          : await searchUser(tabId, documentNumber!, documentCountry!);

        if (isFullAccessRestriction(enrichedUser?.blacklistedData)) {
          closeTab(tabId);
          onFullyBlocked?.(enrichedUser.blacklistedData);
          return;
        }
      }
    }

    const state: RegistroIngresoNavigationState = {
      mode,
      intent: "keep-session",
    };

    history.push({
      pathname: RoutePaths.LogisticParkEntryRegistration,
      state,
    });
  } catch (err: any) {
    console.error("Error validation QR/Pass on scan:", err);
    onError(err?.message || err || "Error al validar el código QR");
  }
};

export const finalizeBuildingMismatchScan = async ({
  pending,
  mode,
  intent,
  session,
  history,
  onFullyBlocked,
}: {
  pending: PendingPassScanData;
  mode: RegistrationMode;
  intent: RegistroIngresoNavigationIntent;
  session: SessionApi;
  history: History;
  onFullyBlocked?: (blacklistedData: IBlackUser) => void;
}): Promise<void> => {
  const { pass, passData, guest } = pending;
  const { appendTab, searchUserByPass, clearSession, closeTab } = session;

  if (intent === "replace-session") {
    clearSession();
  }

  const guestPrefill = {
    id: guest.id,
    firstName: guest.firstname || "",
    lastName: guest.lastname || "",
    documentNumber: guest.dni || "",
    documentCountry: guest.countryCode || "",
    email: guest.email || "",
    phone: guest.phone || "",
    visitorPhoto: guest.facePhoto || null,
    frontDNI: guest.frontDNI || null,
    backDNI: guest.backDNI || null,
  };

  const passDataWithFlag = {
    ...passData,
    isBuildingMismatch: true,
  };
  const tabId = appendTab(guestPrefill, mode);
  const enrichedUser = await searchUserByPass(tabId, pass, undefined, undefined, passDataWithFlag);

  if (isFullAccessRestriction(enrichedUser?.blacklistedData)) {
    closeTab(tabId);
    onFullyBlocked?.(enrichedUser.blacklistedData);
    return;
  }

  const state: RegistroIngresoNavigationState = {
    mode,
    intent: "keep-session",
  };

  history.push({
    pathname: RoutePaths.LogisticParkEntryRegistration,
    state,
  });
};
